import React, { FC, useCallback, useEffect, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './scss/index.scss';
import { APP_URLS } from './consts';
import { LayoutInit } from './components/LayoutInit';
import { Registration } from './components/Registration';
import { Home } from './layouts/pages/Home';
import { MyAccount } from './components/Account/MyAccount/MyAccount';
import { sessionPreviousLocation } from './utils/storage';
import { useAuth } from 'react-oidc-context';
import { Snackbar } from './components/shared/Snackbar';
import { GameDetailsPage } from './layouts/pages/GameDetailsPage';
import { ScrollToTop } from './utils/scroll-to-top';
import ErrorPage from './components/Navigation/ErrorPage/ErrorPage';
import { Cashier } from './components/Account/MyAccount/Cashier';
import { ProvidersCombiner } from './providers';
import { ContentPage } from './layouts/pages/ContentPage';
import { IframePage } from './layouts/pages/IframePage/IframePage';
import { CasinoContent } from './components/CasinoContent';
import { APP_VERSION } from './version';
import { LoggedInMessages } from './components/LoggedInMessages';
import { QuestionnairesList } from './components/Account/MyAccount/Questionnaires/QuestionnaireList';
import { DocumentUploader } from './components/Account/MyAccount/DocumentUploader/DocumentUploader';
import { Login } from './components/Login/Login';
import { useAuthController } from './hooks/auth/useAuthController';
import { CookieBanner } from './components/Compliance/CookieBanner';
import { ProtectionOfCustomerFundsReminder } from './components/Compliance/ProtectionOfCustomerFundsReminder/ProtectionOfCustomerFundsReminder';
import { handleMarketingCookie } from './utils/marketing-preferences-helper';
import { SEO } from './components/seo/SEO';
import { SeonCheck } from './components/SeonCheck/SeonCheck';
import { Promotions } from './components/Layout/Promotions';
import seon from '@seontechnologies/seon-javascript-sdk';
import { NativeEventBus } from './native/NativeEventBus';
import { AppDownloadPrompt } from './components/AppDownload/AppDownloadPrompt';
import { PolicyChecks } from './components/Compliance/PolicyChecks/PolicyChecks';
import { AccountFinancialLimitReviewPrompt } from './components/Compliance/AccountFinancialLimitReviewPrompt/AccountFinancialLimitReviewPrompt';
import { AccountReviewPrompt } from './components/Compliance/AccountReviewPrompt/AccountReviewPrompt';
import { ItvMain } from './layouts/pages/ItvMain';
import { PrizesPage } from './layouts/pages/PrizesPage';
import { BingoPage } from './layouts/pages/BingoPage';
import { SpinsPage } from './layouts/pages/SpinsPage';
import { ItvGameDetailsPage } from './layouts/pages/ItvGameDetailsPage';
import { ItvPromo } from './components/Itv/ItvPromo';

export const App: FC = () => {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isUserAuthenticated = auth?.isAuthenticated;
  const storedPreviousLocation = sessionPreviousLocation.get() as { previousLocation: string };
  const previousLocation = location.state?.previousLocation || storedPreviousLocation?.previousLocation;

  ScrollToTop();

  const [accountOpen, setAccountOpen] = useState<boolean>(false);

  const AuthController: FC = () => {
    useAuthController();

    return null;
  };

  const handleAccountClose = useCallback(
    (isNestedRoute?: boolean): void => {
      setAccountOpen(false);
      navigate(isNestedRoute ? previousLocation || APP_URLS.home : -1);
      sessionPreviousLocation.remove();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate]
  );

  const handleAccountOpen = (): void => {
    // TODO: Refresh wallet totals
    setAccountOpen(true);
  };

  useEffect(() => {
    handleMarketingCookie();

    seon.init();
    window.REACT_APP_VERSION = APP_VERSION || 'dev1';

    if (!NativeEventBus.isNativeBridgeAvailable()) {
      const script = document.createElement('script');

      script.id = 'xtremepush-script';
      script.type = 'text/javascript';
      script.innerHTML = `
        (function(p,u,s,h,e,r,l,i,b) {p['XtremePushObject']=s;p[s]=function(){
          (p[s].q=p[s].q||[]).push(arguments)};i=u.createElement('script');i.async=1;
          i.src=h;b=u.getElementsByTagName('script')[0];b.parentNode.insertBefore(i,b);
        })(window,document,'xtremepush','${process.env.REACT_APP_XP_SDK_URL}');
      `;
      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    const hasAccountQuery = Boolean(location.pathname.includes(APP_URLS.myAccount.home));

    const accOpen = isUserAuthenticated && !accountOpen && hasAccountQuery;

    accOpen && handleAccountOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleAccountClose, isUserAuthenticated]);

  return (
    <>
      <SEO
        title="Online Sports Betting UK & Odds - Betzone™ Sportsbook"
        description="Bet online with Betzone ➜ See the sports betting odds & offers! ✓ Live odds ✓ Bet Builder ✓ Cash Out ✓ Best Odds Guaranteed T&Cs apply"
      />
      <ProvidersCombiner>
        <div id="gameCardZoomPortal" />
        <LayoutInit />
        <Snackbar />
        <QuestionnairesList />
        <Routes location={previousLocation || location}>
          {/* ITV  */}
          <Route path={APP_URLS.itv.root} element={<ItvMain />}>
            <Route path={APP_URLS.itv.gameDetails} element={<ItvGameDetailsPage />} />
            <Route path={APP_URLS.itv.prizesSubPages} element={<PrizesPage />}></Route>
            <Route path={APP_URLS.itv.bingoSubPages} element={<BingoPage />}></Route>
            <Route path={APP_URLS.itv.spinsSubPages} element={<SpinsPage />}></Route>
            <Route path={APP_URLS.itv.promotion} element={<ItvPromo />} />
          </Route>
          {/* ITV  */}

          <Route path={APP_URLS.home} element={<Home />} />
          <Route path={APP_URLS.gameDetails} element={<GameDetailsPage />} />
          <Route path={APP_URLS.promotions} element={<Promotions />} />
          <Route path={APP_URLS.contentPage} element={<ContentPage />} />
          <Route path={APP_URLS.gam} element={<IframePage />} />
          <Route path={APP_URLS.gameLobby.dynamicPage} element={<CasinoContent />} />
          {!isUserAuthenticated && <Route path={APP_URLS.registration} element={<Registration />} />}
          {!isUserAuthenticated && <Route path={APP_URLS.login} element={<Login />} />}
          {previousLocation ? null : <Route path="*" element={<ErrorPage />} />}
        </Routes>
        {isUserAuthenticated ? (
          <>
            <ProtectionOfCustomerFundsReminder />
            <AppDownloadPrompt />
            <AuthController />
            <Cashier />
            <LoggedInMessages />
            <MyAccount isOpen={accountOpen} onClose={handleAccountClose} />
            <DocumentUploader />
            <SeonCheck />
            <PolicyChecks />
            <AccountFinancialLimitReviewPrompt />
            <AccountReviewPrompt />
          </>
        ) : (
          <CookieBanner />
        )}
      </ProvidersCombiner>
    </>
  );
};
